import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { FileDownIcon, Trash2 } from "lucide-react";
import images from "../../assets/images";
import Modal from "../../components/modal/Modal";
import ConfirmModal from "../../components/modal/ConfirmModal";
import CreateFireDrill from "./CreateFireDrill";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../App.css";
import {
  useFireDrills,
  useOrgUsersStore,
  useRegionStore,
} from "../../store/store";
import ViewFireDrill from "./ViewFireDrill";
import FireDrillCalendar from "./FireDrillCalendar";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import type { FireDrill } from "../../types/api";

import { Dropdown } from "../../components/dropdown/Dropdown";
import { usePermissions } from "../../hooks/usePermission";
import {
  EXTERNAL_EMERGENCY_PREPAREDNESS_TESTING_DRILLS,
  FIREDRILL_MODULE,
  HAZARDOUS_MATERIALS_TESTING_DRILLS,
} from "../../constants/modules";
import { useAuth } from "../../auth/AuthProvider";
import { useLocation } from "react-router-dom";

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: FireDrill;
  isCompleted: boolean;
  type: "test_drill" | "evidence_date" | "next_drill";
}

interface OrgUser {
  _id: { $oid: string };
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  mobile: string;
  premise?: string | Array<{ $oid: string }> | string[];
}

const FireDrills: React.FC = () => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [rowData, setRowData] = useState<FireDrill[]>();
  const [mode, setMode] = useState<"create" | "perform">("create");
  const [isViewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [selectedFireDrill, setSelectedFireDrill] = useState<FireDrill | null>(
    null,
  );
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, endDate] = dateRange;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState<OrgUser[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(4);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [drillToDelete, setDrillToDelete] = useState<string | null>(null);

  const {
    fetchFireDrillsData,
    fireDrillsData,
    deleteFireDrill,
    searchFireDrill,
    downloadFireDrill,
  } = useFireDrills();

  const { canRead, canUpdate, canCreate, canDelete } = usePermissions();
  const { fetchOrgUsers, orgUsersData } = useOrgUsersStore();
  const { regions, fetchRegions } = useRegionStore();

  const { roleData } = useAuth();

  const location = useLocation();

  const getModuleName = (): string | undefined => {
    const path = location.pathname;
    if (path.includes("/hazardous-materials")) {
      return "hazardous_materials";
    } else if (path.includes("/external-emergency-preparedness")) {
      return "external_emergency_preparedness";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/risk-assessment")
    ) {
      return "fire_safety";
    }
    return undefined;
  };

  const moduleName = getModuleName();

  const getResponsiblePersonName = (id: string): string => {
    if (!id) return "Not assigned";
    const user = orgUsersData.find((user: OrgUser) => {
      const userId =
        typeof user._id === "object" && "$oid" in user._id
          ? user._id.$oid
          : String(user._id);
      return userId === id;
    });
    return user ? `${user.firstName} ${user.lastName}` : "Not available";
  };

  const formatDateToDDMMYYYY = (date: Date | null): string => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const searchFireDrillByDateRange = async (
    start: Date | null,
    end: Date | null,
  ) => {
    try {
      setLoading(true);
      setError(null);

      if (start && end) {
        const formattedStart = formatDateToDDMMYYYY(start);
        const formattedEnd = formatDateToDDMMYYYY(end);

        await searchFireDrill(
          {
            from_date: formattedStart,
            to_date: formattedEnd,
          },
          moduleName,
        );
      } else {
        await fetchFireDrillsData(moduleName);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred",
      );
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgUsers();
  }, []);

  useEffect(() => {
    if (!regions.length) {
      fetchRegions();
    }
  }, []);

  useEffect(() => {
    searchFireDrillByDateRange(startDate, endDate);
  }, [startDate, endDate, moduleName]);
  /*
    useEffect(() => {
      if (!Array.isArray(fireDrillsData)) {
        setRowData([]);
        return;
      }
  
      let filtered = [...fireDrillsData];
  
      // Region Filter
      if (selectedRegion) {
        const selectedRegionObj = regions.find(
          (r: any) => String(r._id) === String(selectedRegion),
        );
  
        if (selectedRegionObj) {
          filtered = filtered.filter(
            (drill: any) =>
              drill.region?.trim() === selectedRegionObj.regionName?.trim(),
          );
        }
      }
  
      // Premises Filter
      if (selectedPremises) {
        filtered = filtered.filter(
          (drill: any) => String(drill.premisesId) === String(selectedPremises),
        );
      }
  
      setRowData(filtered);
    }, [fireDrillsData, selectedRegion, selectedPremises, regions]); 
    */

  useEffect(() => {
    setRowData(fireDrillsData);
  }, [fireDrillsData]);

  const handlePremiseChange = async (value: string) => {
    setSelectedPremises(value);
    const selectedRegionObj = regions.find(
      (r: any) => String(r._id) === String(selectedRegion),
    );

    await fetchFireDrillsData(moduleName, selectedRegionObj?.regionName, value);
  };

  const handleRegionChange = async (region: string) => {
    setSelectedRegion(region);
    setSelectedPremises("");

    const selectedRegionObj = regions.find(
      (r: any) => String(r._id) === String(region),
    );
    await fetchFireDrillsData(moduleName, selectedRegionObj?.regionName, "");
  };

  const handleEmailCalendar = (month: string, events: CalendarEvent[]) => {
  };

  const handleEdit = (fireDrill: FireDrill) => {
    setSelectedFireDrill(fireDrill);
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedFireDrill(null);
    setIsEdit(false);
    setModalOpen(true);
  };

  const handleView = (fireDrill: FireDrill) => {
    setSelectedFireDrill(fireDrill);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setDrillToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!drillToDelete) return;
    try {
      await deleteFireDrill(drillToDelete);
    } catch (error) {
      console.error("Error deleting drill:", error);
    } finally {
      setIsDeleteModalOpen(false);
      setDrillToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDrillToDelete(null);
  };

  const handleDownload = async () => {
    try {
      const response = await downloadFireDrill(moduleName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${
          moduleName === "hazardous_materials"
            ? "hazardous_materials"
            : moduleName === "external_emergency_preparedness"
              ? "external_emergency_preparedness"
              : "fire"
        }_drills_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading:", error);
    }
  };

  const actionCellRenderer = (params: ICellRendererParams) => {
    const isCompleted = params.data.status === "completed";
    const moduleType = params.data.moduleType || "fire_safety";

    // Determine which permissions module to use based on drill type
    const permissionModule =
      moduleType === "hazardous_materials"
        ? HAZARDOUS_MATERIALS_TESTING_DRILLS // You'll need to define this constant
        : moduleType === "external_emergency_preparedness"
          ? EXTERNAL_EMERGENCY_PREPAREDNESS_TESTING_DRILLS // You'll need to define this constant
          : FIREDRILL_MODULE;

    return (
      <div className="flex gap-2 mt-2 items-center">
        {canRead(permissionModule) && (
          <button
            className="focus:outline-none"
            onClick={() => handleView(params.data)}
          >
            <img className="iconTd" src={images.viewIcon} alt="View" />
          </button>
        )}
        {canUpdate(permissionModule) && (
          <button
            className={`focus:outline-none ${
              isCompleted ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={isCompleted ? undefined : () => handleEdit(params.data)}
            disabled={isCompleted}
          >
            <img className="iconTd" src={images.editIcon} alt="Edit" />
          </button>
        )}
        {canDelete(permissionModule) && (
          <button
            className="focus:outline-none"
            onClick={() => handleDeleteClick(params.data._id)}
          >
            <Trash2 size={22} className="text-red-500" />
          </button>
        )}
      </div>
    );
  };
  const handleViewAttendees = (fireDrill: FireDrill) => {
    if (fireDrill.users && orgUsersData) {
      const attendees = orgUsersData?.filter((user: any) =>
        fireDrill?.users?.includes(
          typeof user._id === "object" && "$oid" in user._id
            ? user._id.$oid
            : String(user._id),
        ),
      );
      setSelectedAttendees(attendees);
      setIsAttendeesModalOpen(true);
      setCurrentPage(1); // Reset to first page when opening modal
    }
  };

  const totalFireDrills = fireDrillsData.length;

  const upcomingDrills = Array.isArray(fireDrillsData)
    ? fireDrillsData.filter((drill) => {
        if (!drill.date_of_test_drills) return false;
        const dateStr =
          typeof drill.date_of_test_drills === "string"
            ? drill.date_of_test_drills
            : drill.date_of_test_drills
              ? formatDateToDDMMYYYY(drill.date_of_test_drills)
              : "";
        if (!dateStr) return false;
        const [day, month, year] = dateStr.split("/");
        const drillDate = new Date(`${year}-${month}-${day}`);
        const today = new Date();
        drillDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return drillDate > today;
      })
    : [];
  const participationRates = Array.isArray(fireDrillsData)
    ? fireDrillsData.map((drill) => {
        const rate =
          typeof drill.participation_rate === "string"
            ? parseFloat(drill.participation_rate)
            : Number(drill.participation_rate);
        return isNaN(rate) ? 0 : rate;
      })
    : [];

  const avgParticipationRate =
    participationRates.length > 0
      ? Math.round(
          participationRates.reduce((acc, curr) => acc + curr, 0) /
            participationRates.length,
        )
      : 0;

  const drillDurations = Array.isArray(fireDrillsData)
    ? fireDrillsData
        .map((drill) => Number(drill?.average_drill))
        .filter((duration) => !isNaN(duration))
    : [];

  const avgDrillDuration =
    drillDurations.length > 0
      ? Math.round(
          (drillDurations.reduce((acc, curr) => acc + curr, 0) /
            drillDurations.length) *
            100,
        ) / 100
      : 0;

  const statusCellRenderer = (params: ICellRendererParams) => {
    const status = params.value || "scheduled";
    const moduleType = params.data.moduleType || "fire_safety";
    const isHazardousMaterials = moduleType === "hazardous_materials";
    const isExternalEmergencyPreparedness =
      moduleType === "external_emergency_preparedness";

    // Check user's role
    const userRole = roleData?.roleName || "";

    // Determine if user can perform this specific drill type
    const canPerformThisDrill = isHazardousMaterials
      ? userRole === "perform-hazardous-testing-drill" || userRole === "admin" // Add other roles as needed
      : isExternalEmergencyPreparedness
        ? userRole ===
            "perform-external-emergency-preparedness-testing-drill" ||
          userRole === "admin" // Add other roles as needed
        : userRole === "perform-drill" || userRole === "admin"; // Add other roles as needed

    const statusClass =
      status === "completed"
        ? "bg-green-100 text-green-800"
        : "bg-[#0caba8] text-white";

    const handleStatusClick = () => {
      if (status === "scheduled" && canPerformThisDrill) {
        setSelectedFireDrill(params.data);
        setIsEdit(true);
        setModalOpen(true);
        setMode("perform");
      }
    };

    return (
      <>
        {status === "scheduled" && canPerformThisDrill ? (
          <button onClick={handleStatusClick} className="cursor-pointer">
            <span className={`px-2 py-1 rounded-full text-xs ${statusClass}`}>
              {status.toUpperCase()}
            </span>
          </button>
        ) : (
          <span className={`px-2 py-1 rounded-full text-xs ${statusClass}`}>
            {status.toUpperCase()}
          </span>
        )}
      </>
    );
  };

  const columnDefs: ColDef[] = useMemo(
    () =>
      [
        {
          headerName: "Region",
          field: "region",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
        },
        {
          headerName: "Premise",
          field: "premise",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
        },
        {
          headerName: "Date of test/drill",
          field: "date_of_test_drills",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
        },
        {
          headerName: "Status",
          field: "status",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
          cellRenderer: statusCellRenderer,
        },
        {
          headerName: "Responsible",
          field: "person_responsibility",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
          valueGetter: (params: any) => {
            return getResponsiblePersonName(params.data.person_responsibility);
          },
        },
        {
          headerName: "Created by",
          field: "created_by",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
        },
        // Hide attendees for perform-drill and hazardous-drill roles
        ![
          "perform-drill",
          "perform-hazardous-testing-drill",
          "perform-external-emergency-preparedness-testing-drill",
        ].includes(roleData?.roleName || "") && {
          headerName: "Attendees",
          field: "users",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
          cellRenderer: (params: ICellRendererParams) => {
            const attendeesCount = params.data.users?.length || 0;
            return (
              <button
                onClick={() => handleViewAttendees(params.data)}
                className="text-teal-600 hover:text-teal-800 font-medium"
                disabled={attendeesCount === 0}
              >
                {attendeesCount > 0 ? `View (${attendeesCount})` : "None"}
              </button>
            );
          },
        },
        // Show action column only for non-drill roles
        ![
          "perform-drill",
          "perfrom-hazardous-testing-drill",
          "perform-external-emergency-preparedness-testing-drill",
        ].includes(roleData?.roleName || "") && {
          headerName: "Action",
          field: "actions",
          flex: 1,
          cellStyle: { padding: "10px 10px 0 10px" },
          cellRenderer: actionCellRenderer,
        },
      ].filter(Boolean) as ColDef[],
    [roleData, orgUsersData],
  );

  // Pagination calculations
  const totalPages = Math.ceil(selectedAttendees.length / itemsPerPage);
  const paginatedAttendees = selectedAttendees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <>
      <div className="px-2 py-4 mt-14 w-full h-full">
        <div className="tableSearchBlock d-flex justify-content-between">
          <div className="leftTableSearchBlock">
            <h2>
              {moduleName === "hazardous_materials"
                ? "Hazardous Testing / Drills"
                : "Testing / Drills"}
            </h2>
          </div>
          <div className="flex gap-4">
            {!showCalendar && (
              <>
                <Dropdown
                  selectedValue={selectedRegion}
                  onChange={handleRegionChange}
                  type="region"
                />
                <Dropdown
                  selectedValue={selectedPremises}
                  onChange={handlePremiseChange}
                  type="premises"
                  selectedRegion={selectedRegion}
                />
              </>
            )}

            <button
              className="bg-[#0fa9a6] text-white px-5 py-2 rounded-full font-semibold hover:bg-[#197f7d] transition-all focus:outline-none"
              onClick={() => setShowCalendar(!showCalendar)}
            >
              {showCalendar ? "Show Table" : "Show Calendar"}
            </button>
          </div>
        </div>

        {showCalendar ? (
          <FireDrillCalendar
            onEmailCalendar={handleEmailCalendar}
            dateRange={dateRange}
          />
        ) : (
          <>
            <div className="cardNum mt-4 mb-4">
              <div className="row">
                <div className="col-sm-3">
                  <div className="card">
                    <div className="cardIconCont">
                      <span className="font-medium text-md">
                        {moduleName === "hazardous_materials" ||
                        moduleName === "external_emergency_preparedness"
                          ? "Total Drills"
                          : "Total Drills"}
                      </span>
                      <h3>{totalFireDrills}</h3>
                    </div>
                    <div className="cardIcon cardIconColor1">
                      <img
                        className="iconTd"
                        src={images.premisesIcon}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-3">
                  <div className="card">
                    <div className="cardIconCont">
                      <span className="font-medium text-md">
                        {moduleName === "hazardous_materials" ||
                        moduleName === "external_emergency_preparedness"
                          ? "Upcoming Drills"
                          : "Upcoming Drills"}
                      </span>
                      <h3>{upcomingDrills.length}</h3>
                    </div>
                    <div className="cardIcon cardIconColor2">
                      <img
                        className="iconTd"
                        src={images.premisesIcon}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-3">
                  <div className="card">
                    <div className="cardIconCont">
                      <span className="font-medium text-md">
                        Average Participation Rate (%)
                      </span>
                      <h3>{avgParticipationRate}%</h3>
                    </div>
                    <div className="cardIcon cardIconColor3">
                      <img
                        className="iconTd"
                        src={images.premisesIcon}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-3">
                  <div className="card">
                    <div className="cardIconCont">
                      <span className="font-medium text-md">
                        {moduleName === "hazardous_materials" ||
                        moduleName === "external_emergency_preparedness"
                          ? "Average Response Duration"
                          : "Average Drill Duration"}
                      </span>
                      <h3>{avgDrillDuration} Min</h3>
                    </div>
                    <div className="cardIcon cardIconColor4">
                      <img
                        className="iconTd"
                        src={images.premisesIcon}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-[#ffffff] rounded-md">
              <div className="flex flex-wrap justify-between items-center gap-4 p-3">
                <div className="flex items-center gap-3">
                  <div>
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update: [Date | null, Date | null]) => {
                        setDateRange(update);
                      }}
                      wrapperClassName="datePicker"
                      placeholderText="DD/MM/YYYY - DD/MM/YYYY"
                      dateFormat="dd/MM/yyyy"
                      className="w-[250px] z-1 pl-10 border-2 border-[#0fa9a6] rounded p-2 focus:outline-none focus:border-[#197f7d] placeholder:text-black placeholder:opacity-50 placeholder:font-semibold"
                      isClearable={true}
                      popperClassName="z-50"
                    />
                  </div>
                  <button
                    className="focus:outline-none"
                    onClick={handleDownload}
                  >
                    <FileDownIcon size={35} color="teal" />
                  </button>
                </div>
                {canCreate(FIREDRILL_MODULE) && (
                  <button
                    className="bg-[#0fa9a6] text-white px-5 py-2 rounded-full font-semibold hover:bg-[#197f7d] transition-all"
                    onClick={handleCreate}
                  >
                    <div className="flex items-center gap-2">
                      <img src={images.addPremiessIcon} alt="" width={25} />
                      {moduleName === "hazardous_materials" ||
                      moduleName === "external_emergency_preparedness"
                        ? "CREATE DRILL"
                        : "CREATE DRILL"}
                    </div>
                  </button>
                )}
              </div>
              <div
                className="ag-theme-alpine"
                style={{
                  height: "calc(100vh - 300px)",
                  width: "100%",
                  marginTop: "1rem",
                }}
              >
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    Loading data...
                  </div>
                ) : error ? (
                  <div className="flex justify-center items-center h-full text-red-500">
                    Error: {error}
                  </div>
                ) : (
                  <AgGridReact
                    rowData={rowData || []}
                    columnDefs={columnDefs}
                    rowHeight={60}
                    headerHeight={60}
                    onGridReady={(params) => params.api.sizeColumnsToFit()}
                    suppressCellFocus={true}
                    animateRows={true}
                  />
                )}
              </div>
            </div>
          </>
        )}

        <Modal
          isOpen={isModalOpen}
          closeButton={false}
          modalClass="bg-white w-full max-w-6xl max-h-[90vh] rounded-xl shadow-lg flex flex-col"
          onClose={() => {
            setModalOpen(false);
            setIsEdit(false);
          }}
        >
          <CreateFireDrill
            onClose={() => {
              setModalOpen(false);
              setIsEdit(false);
              setMode("create");
            }}
            fireDrillToEdit={
              isEdit && selectedFireDrill
                ? {
                    ...selectedFireDrill,
                    premisesId: selectedFireDrill.premisesId ?? "",
                    status: "completed",
                    area: selectedFireDrill.area ?? "",
                  }
                : null
            }
            isEdit={isEdit}
            initialMode={mode}
            moduleName={moduleName}
          />
        </Modal>

        <Modal
          isOpen={isViewModalOpen}
          closeButton={false}
          modalClass="bg-white w-full max-w-6xl max-h-[90vh] rounded-xl shadow-lg flex flex-col"
          onClose={() => setViewModalOpen(false)}
        >
          {selectedFireDrill && (
            <ViewFireDrill
              fireDrill={selectedFireDrill}
              onClose={() => setViewModalOpen(false)}
            />
          )}
        </Modal>

        {roleData?.roleName !== "perform-drill" &&
          roleData?.roleName !== "perform-hazardous-testing-drill" &&
          roleData?.roleName !==
            "perform-external-emergency-preparedness-testing-drill" && (
            <Modal
              isOpen={isAttendeesModalOpen}
              closeButton={false}
              modalClass="bg-white w-full max-w-4xl max-h-[80vh] rounded-xl shadow-lg flex flex-col"
              onClose={() => setIsAttendeesModalOpen(false)}
            >
              <div className="flex-none px-6 pt-6">
                <h2 className="text-2xl font-semibold">Attendees List</h2>
                <hr className="mt-2 border-2 border-gray-400" />
              </div>

              <div className="flex-1 overflow-y-auto px-6 pb-6">
                {selectedAttendees.length > 0 ? (
                  <div className="mt-1">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-3 font-semibold text-sm text-gray-700">
                            Name
                          </th>
                          <th className="p-3 font-semibold text-sm text-gray-700">
                            Email
                          </th>
                          <th className="p-3 font-semibold text-sm text-gray-700">
                            Mobile
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedAttendees.map((user, index) => (
                          <tr
                            key={index}
                            className={`border-b border-gray-200 ${
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }`}
                          >
                            <td className="p-3 text-sm text-gray-800">
                              {user.firstName} {user.lastName}
                            </td>
                            <td className="p-3 text-sm text-gray-600">
                              {user.email}
                            </td>
                            <td className="p-3 text-sm text-gray-600">
                              {user.mobile || "N/A"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex justify-between items-center mt-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Show</span>
                        <select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          className="border border-gray-300 rounded p-1 text-sm"
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                        <span className="text-sm text-gray-600">per page</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`px-3 py-1 rounded-full text-sm ${
                            currentPage === 1
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-[#0fa9a6] text-white hover:bg-[#197f7d]"
                          }`}
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600 flex items-center">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`px-3 py-1 rounded-full text-sm ${
                            currentPage === totalPages
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-[#0fa9a6] text-white hover:bg-[#197f7d]"
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 mt-8">
                    No attendees found
                  </p>
                )}
              </div>

              <div className="flex-none py-3 px-6 bg-gray-600 flex justify-end gap-4 rounded-bl-xl rounded-br-xl">
                <button
                  type="button"
                  className="bg-gray-300 text-gray-800 px-6 py-2 rounded-full"
                  onClick={() => setIsAttendeesModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </Modal>
          )}
      </div>
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="Are you sure you want to delete this drill?"
        confirmLabel="YES"
        cancelLabel="NO"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default FireDrills;
